import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, CalendarDays, Users, Settings as SettingsIcon,
  Bell, LogOut, Activity, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ReminderNudge from "@/components/ReminderNudge";

const ADMIN_NAV = [
  { to: "/", label: "Daily", icon: LayoutDashboard, end: true, testid: "nav-daily" },
  { to: "/monthly", label: "Monthly", icon: CalendarDays, testid: "nav-monthly" },
  { to: "/managers", label: "Managers", icon: Users, testid: "nav-managers" },
  { to: "/audit", label: "Audit Trail", icon: Bell, testid: "nav-audit" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const MANAGER_NAV = [
  { to: "/", label: "My EOD", icon: Send, end: true, testid: "nav-my" },
  { to: "/monthly", label: "My History", icon: CalendarDays, testid: "nav-history" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = user?.role === "admin" ? ADMIN_NAV : MANAGER_NAV;
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex">
      {user?.role === "admin" && <ReminderNudge />}
      <aside className="w-60 shrink-0 border-r border-[#1f1f23] bg-[#0c0c0e] flex flex-col">
        <div className="p-5 border-b border-[#1f1f23]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Activity className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <div className="font-display font-semibold leading-tight">EOD Ops</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Report Automation</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-[#18181b] border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1f1f23]">
          <div className="mb-2 px-2">
            <div className="text-sm text-zinc-100">{user?.name}</div>
            <div className="text-xs text-zinc-500">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-indigo-400 mt-0.5">
              {user?.role === "admin" ? "Master Admin" : "Manager"}
            </div>
          </div>
          <Button variant="ghost" size="sm" data-testid="logout-button"
            onClick={logout}
            className="w-full justify-start text-zinc-400 hover:text-zinc-100">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
